import React, { useState, useEffect, useRef, useImperativeHandle } from 'react';
import { X, Check } from 'lucide-react';
import { Input } from './ui/Input';
import { useTagsApi } from '../apis/tags-apis';
import { Tag } from '../types';

interface TagSelectorProps {
  selectedTags: string[];
  onChange: (tags: string[]) => void;
  readOnly?: boolean;
  className?: string;
}

interface TagSelectorRef {
  getPendingTags: () => string[];
  processPendingTags: () => Promise<void>;
}

export const TagSelector = React.forwardRef<TagSelectorRef, TagSelectorProps>(({
  selectedTags = [],
  onChange,
  readOnly = false,
  className = '',
}, ref) => {
  const [inputValue, setInputValue] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [allTags, setAllTags] = useState<Tag[]>([]);
  const [filteredTags, setFilteredTags] = useState<Tag[]>([]);
  const [pendingTags, setPendingTags] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const tagsApi = useTagsApi();

  useEffect(() => {
    fetchTags();
  }, []);

  useEffect(() => {
    // Filter tags based on input value
    if (inputValue.trim()) {
      const filtered = allTags.filter(tag => 
        tag.tagName.toLowerCase().includes(inputValue.toLowerCase())
      );
      setFilteredTags(filtered);
    } else {
      setFilteredTags(allTags);
    }
  }, [inputValue, allTags]);

  useEffect(() => {
    // Add event listener to detect clicks outside the component
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const fetchTags = async () => {
    try {
      setIsLoading(true);
      const tags = await tagsApi.getTags();
      setAllTags(tags);
      setFilteredTags(tags);
    } catch (error) {
      console.error('Error fetching tags:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    // Remove spaces in real-time as the user types
    const value = e.target.value.replace(/\s+/g, '');
    setInputValue(value);
    if (!isOpen) {
      setIsOpen(true);
    }
  };

  const handleInputFocus = () => {
    if (!readOnly) {
      setIsOpen(true);
    }
  };

  const handleTagSelect = (tagName: string) => {
    if (readOnly) return;

    if (!selectedTags.includes(tagName)) {
      const newTags = [...selectedTags, tagName];
      onChange(newTags);
      
      // If this was a pending tag, remove it from pending list
      if (pendingTags.includes(tagName)) {
        setPendingTags(prev => prev.filter(tag => tag !== tagName));
      }
    }
    setInputValue('');
    setIsOpen(false);
  };

  const handleAddPendingTag = () => {
    if (!inputValue.trim() || readOnly) return;
    
    // Remove all whitespace and ensure tag is valid
    const normalizedTagName = inputValue.trim().replace(/\s+/g, '');
    
    if (normalizedTagName === '') return;
    
    // Check if tag already exists
    const existingTag = allTags.find(
      tag => tag.tagName.toLowerCase() === normalizedTagName.toLowerCase()
    );
    
    if (existingTag) {
      // Use existing tag
      handleTagSelect(existingTag.tagName);
    } else {
      // Add as pending tag
      if (!selectedTags.includes(normalizedTagName) && !pendingTags.includes(normalizedTagName)) {
        // Track this as a pending tag to be created on form submission
        setPendingTags(prev => [...prev, normalizedTagName]);
        // Add to selected tags
        const newTags = [...selectedTags, normalizedTagName];
        onChange(newTags);
      }
      setInputValue('');
      setIsOpen(false);
    }
  };

  const handleRemoveTag = (tagToRemove: string) => {
    if (readOnly) return;
    
    // Remove from selected tags
    const newTags = selectedTags.filter(tag => tag !== tagToRemove);
    onChange(newTags);
    
    // Also remove from pending tags if it was there
    if (pendingTags.includes(tagToRemove)) {
      setPendingTags(prev => prev.filter(tag => tag !== tagToRemove));
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (readOnly) return;

    if (e.key === 'Enter' && inputValue.trim()) {
      e.preventDefault();
      handleAddPendingTag();
    } else if (e.key === 'Escape') {
      setIsOpen(false);
    }
  };

  // Helper function to get tag CSS class with different styling for pending tags
  const getTagClassName = (tag: string) => {
    const isPending = pendingTags.includes(tag);
    return `flex items-center gap-1 ${
      isPending 
        ? 'bg-blue-50 text-blue-800 border border-dashed border-blue-300' 
        : 'bg-blue-100 text-blue-800'
    } px-2 py-1 rounded-md text-sm`;
  };

  // Expose methods to parent components via ref
  useImperativeHandle(ref, () => ({
    getPendingTags: () => pendingTags,
    processPendingTags: async () => {
      try {
        // Skip if no pending tags
        if (pendingTags.length === 0) return;
        
        // Process each pending tag
        for (const tagName of pendingTags) {
          try {
            // Create the tag directly
            const newTag = await tagsApi.createTag(tagName);
            // Add to allTags
            setAllTags(prev => [...prev, newTag]);
          } catch (error) {
            console.error(`Error creating tag "${tagName}":`, error);
          }
        }
        
        // Clear the pending tags array
        setPendingTags([]);
        
        // Refresh tags from server
        fetchTags();
      } catch (error) {
        console.error('Error processing pending tags:', error);
      }
    }
  }));

  return (
    <div
      ref={containerRef}
      className={`relative w-full ${className}`}
    >
      <div className={`flex flex-wrap gap-2 p-2 border rounded-md ${readOnly ? 'bg-gray-50' : 'bg-white'}`}>
        {selectedTags.map(tag => (
          <div
            key={tag}
            className={getTagClassName(tag)}
          >
            <span>{tag}</span>
            {!readOnly && (
              <button
                type="button"
                onClick={() => handleRemoveTag(tag)}
                className="text-blue-800 hover:text-blue-600"
              >
                <X size={14} />
              </button>
            )}
          </div>
        ))}
        
        {!readOnly && (
          <div className="flex-1 min-w-[120px]">
            <Input
              ref={inputRef}
              type="text"
              value={inputValue}
              onChange={handleInputChange}
              onFocus={handleInputFocus}
              onKeyDown={handleKeyDown}
              placeholder="Type to search or add tags..."
              className="border-0 shadow-none focus-visible:ring-0 p-0 text-sm w-full"
            />
          </div>
        )}
      </div>

      {isOpen && !readOnly && (
        <div className="absolute z-10 mt-1 w-full bg-white border rounded-md shadow-lg max-h-60 overflow-auto">
          {isLoading ? (
            <div className="p-2 text-gray-500 text-center">Loading tags...</div>
          ) : (
            <>
              {filteredTags.length > 0 ? (
                <>
                  {filteredTags.map(tag => (
                    <div
                      key={tag.tagId}
                      className={`p-2 hover:bg-gray-100 cursor-pointer flex items-center justify-between ${
                        selectedTags.includes(tag.tagName) ? 'bg-blue-50' : ''
                      }`}
                      onClick={() => handleTagSelect(tag.tagName)}
                    >
                      <span>{tag.tagName}</span>
                      {selectedTags.includes(tag.tagName) && <Check size={16} className="text-blue-500" />}
                    </div>
                  ))}
                </>
              ) : inputValue.trim() ? (
                <div
                  className="p-2 hover:bg-gray-100 cursor-pointer flex items-center text-blue-600"
                  onClick={handleAddPendingTag}
                >
                  <span>Add tag: "{inputValue}"</span>
                </div>
              ) : (
                <div className="p-2 text-gray-500 text-center">No tags found</div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
});

export default TagSelector;